// PlatformBadge — small colored chip identifying a channel's source platform.
// Used in lists / cards where the operator needs to spot the platform at a
// glance (e.g. "is this an X tweet or a YouTube video"). Centralized here so
// when a new platform lands (TikTok upgrade, Telegram, etc.) the visual
// language stays consistent.

import { cn } from "@/lib/utils";

interface Props {
  platform: string;
  // Compact = just the 2-letter chip. Full = chip + readable label next to it.
  // Default compact; rows with little space (table cells) prefer it.
  variant?: "compact" | "full";
  className?: string;
}

const STYLES: Record<string, { code: string; label: string; cls: string }> = {
  // DB stores "twitter" for X. Accept "x" too (defensive).
  youtube: { code: "YT", label: "YouTube", cls: "bg-red-100 text-red-700 ring-1 ring-red-200" },
  twitter: { code: "X", label: "X", cls: "bg-zinc-900 text-zinc-50" },
  x: { code: "X", label: "X", cls: "bg-zinc-900 text-zinc-50" },
  tiktok: { code: "TT", label: "TikTok", cls: "bg-pink-100 text-pink-700 ring-1 ring-pink-200" },
  // v2.9.0 — generic RSS/Atom feed (Medium, Substack, etc).
  rss: { code: "RSS", label: "RSS", cls: "bg-orange-100 text-orange-700 ring-1 ring-orange-200" },
  // v2.9.0 — Foresight News column.
  foresight: { code: "FS", label: "Foresight", cls: "bg-blue-100 text-blue-700 ring-1 ring-blue-200" },
  // v2.9.0 — PA News (panewslab.com) column.
  panews: { code: "PA", label: "PA News", cls: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200" },
};

export function PlatformBadge({ platform, variant = "compact", className }: Props) {
  const s = STYLES[platform.toLowerCase()] ?? {
    code: platform.slice(0, 2).toUpperCase(),
    label: platform,
    cls: "bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200",
  };
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <span
        className={cn(
          "inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-bold leading-none",
          s.cls,
        )}
        title={s.label}
      >
        {s.code}
      </span>
      {variant === "full" && <span className="text-xs text-muted-foreground">{s.label}</span>}
    </span>
  );
}
